import { useMemo } from 'react';

import { Select } from 'antd';

import { formatDaysToDateRange } from '@/shared/lib/format';
import { formatMetricValue } from '@/shared/lib/utils';
import { DonutChart } from '@/shared/ui/DonutChart';

import { QUALITY_CHART_COLOR_MAP, useTableQualityDataQuery as useNewDataQualityQuery } from '@/entities/quality';

import { useDateOption } from '@/features/quality/model/useDateOption';
import { CardHeader } from '@/features/quality/ui/dashboard/CardHeader';
import { DonutLabelList } from '@/features/quality/ui/dashboard/DonutLabelList';

import styles from './DataTestCaseResults.module.css';

const TITLE = '신규 데이터 품질 현황';
const TOOLTIP_DESCRIPTION =
  '선택한 기간에 적재된 신규로 Row의 품질 평가 결과입니다. 결측 여부 기준으로 평가하여 양호(결측 없음), 미흡(결측 있음), 판정 불가로 집계합니다';

export const DataTestCaseResults = () => {
  const { DATE_OPTIONS, selectedDateValue, handleSelectDateValue } = useDateOption();

  const { startDate, endDate } = formatDaysToDateRange(selectedDateValue, 1);

  const { data: apiResponse, isLoading: showLoading } = useNewDataQualityQuery(
    {
      fromDate: startDate,
      toDate: endDate,
      page: 1,
      pageSize: 20,
    },
    true
  );

  const chartData = useMemo(() => {
    if (!apiResponse?.summary) {
      return [
        { name: '양호', value: 0 },
        { name: '미흡', value: 0 },
        { name: '판정 불가', value: 0 },
      ];
    }

    const { summary } = apiResponse;
    return [
      { name: '양호', value: summary.totalGoodRows },
      { name: '미흡', value: summary.totalPoorRows },
      { name: '판정 불가', value: summary.totalUnknownRows },
    ];
  }, [apiResponse]);

  const labelListData = useMemo(() => {
    return chartData.map((item) => ({
      icon: (
        <div
          className={styles.dot}
          style={{ backgroundColor: QUALITY_CHART_COLOR_MAP[item.name as keyof typeof QUALITY_CHART_COLOR_MAP] }}
        />
      ),
      label: item.name,
      value: formatMetricValue(item.value),
    }));
  }, [chartData]);

  return (
    <>
      <CardHeader
        title={TITLE}
        tooltipDescription={TOOLTIP_DESCRIPTION}
        rightContent={
          <Select
            options={DATE_OPTIONS}
            value={selectedDateValue}
            onChange={handleSelectDateValue}
            className={styles.select}
          />
        }
      />
      <DonutChart
        data={chartData}
        showLoading={showLoading}
        isTotalVisible={true}
        isLegendVisible={false}
        colorMap={QUALITY_CHART_COLOR_MAP}
        containerClassName={styles.chartContainer}
        style={{ width: '200px', height: '200px' }}
      />
      <DonutLabelList items={labelListData} />
    </>
  );
};
